import { useEffect, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../store/useAuthStore'
import { useOnlineModeStore } from '../../store/useOnlineModeStore'
import { useFillQueueStore } from '../../store/useFillQueueStore'
import { useFillPromptStore } from '../../store/useFillPromptStore'
import { useFillAttemptStore, attemptKey } from '../../store/useFillAttemptStore'
import { useIncompleteDataStore } from '../../store/useIncompleteDataStore'

// Napojení hooku na dotahování chybějících dat (viz doc/plans/to-fill-data-fetching.md,
// krok 8; fill-attempt-cache.md).
//
//   useToFill(queryKey, data, mergeFilled, retryCtx?)   — zdroj to_fill je React Query
//                                                          ({ result, toFill }); po každém fill POSTu
//                                                          se řada z odpovědi mergne do query cache
//                                                          (žádný refetch celé query).
//   useChartToFill(screenKey, meta, retryCtx?)          — zdroj to_fill je meta.toFill z grafu;
//                                                          graf nemá data k merge, po dotažení
//                                                          se přenačte přes useChartFillBust.
//   useChartsToFill(screenKey, chartKeys, retryCtx?)    — víc grafů na obrazovce (skupiny, období);
//                                                          každý graf se přenačte sám, jakmile
//                                                          se dotáhnou všechny JEHO řady.
//
// retryCtx = { gran: '10M'|'HLY'|'DLY'|'MONTH'|'YEAR', live: bool } — pro cache pokusů.
//
// Podle režimu (useOnlineModeStore):
//   'off'  → nedotahuj; jen zapiš do registru neúplných řad (IncompleteDataBadge nabídne dodatečně)
//   'auto' → rovnou do fronty (useFillQueueStore)
//   'ask'  → nabídne do useFillPromptStore, rozhodne dialog

const sig = (it) => `${it.endpoint}|${JSON.stringify(it.params)}`
const toEntry = (it, retryCtx) => ({ key: attemptKey(it), item: it, retryCtx })

// Položka se právě aktivně řeší (čeká ve frontě / nabídnutá v dialogu) —
// nenabízet znovu, ale ani nedávat do štítku „neúplná data" (řeší se).
const beingHandled = (screenKey, item) =>
  useFillQueueStore.getState().has(screenKey, item) ||
  useFillPromptStore.getState().has(screenKey, item)

// Po pokusu běží pauza T min — nenabízet, ale DO štítku ano (uživatel může
// vynutit dřív přes „Dotáhnout").
const inRetryPause = (item) =>
  useFillAttemptStore.getState().isBlocked(attemptKey(item))

// Registr „aplikátorů" výsledku fill POSTu per obrazovka (screenKey tabulky).
// Používá ho IncompleteDataBadge („Dotáhnout"), který jinak k mergeFilled
// daného hooku přístup nemá. Graf aplikátor nemá (přenačte se přes useChartFillBust).
const appliers = new Map()
export const getFillApplier = (screenKey) => appliers.get(screenKey)

// Zaregistruje aplikátor pro screenKey (vždy aktuální closure přes ref).
function useRegisterApplier(screenKey, apply) {
  const applyRef = useRef(apply)
  applyRef.current = apply
  useEffect(() => {
    const fn = (...args) => applyRef.current(...args)
    appliers.set(screenKey, fn)
    return () => { if (appliers.get(screenKey) === fn) appliers.delete(screenKey) }
  }, [screenKey])
}

// Společné jádro.
//   screenKey — JSON string
//   toFill    — pole položek
//   onSettled(item, resp, filled) — po KAŽDÉM pokusu (i filled:false / chyba): aplikace
//                           výsledku do zdroje (merge řady + úklid toFill), ať ji Effect 1
//                           znovu nenabízí. resp = null při chybě (403, síť).
function useToFillCore(screenKey, toFill, retryCtx, onSettled) {
  const canFill = useAuthStore((s) => s.canFill)
  const mode = useOnlineModeStore((s) => s.mode)

  // Aktuální hodnoty pro effect, který běží při každém renderu bez deps.
  const ref = useRef()
  ref.current = { screenKey, toFill, retryCtx, onSettled, canFill, mode }

  // ── Effect 1: zpracuj to_fill (běží každý render, žádné deps, žádný cleanup) ──
  useEffect(() => {
    const { screenKey: sk, toFill: tf, retryCtx: rc, onSettled: settled, canFill: cf, mode: md } = ref.current
    const incomplete = useIncompleteDataStore.getState()

    // Žádné právo / žádné to_fill → obrazovka je „úplná", registr vyčistit.
    if (!cf || !tf?.length) {
      incomplete.set(sk, [])
      return
    }

    if (md === 'off') {
      // Nedotahuj — jen eviduj neúplnost pro badge.
      incomplete.set(sk, tf.map((it) => toEntry(it, rc)))
      return
    }

    // fresh   = ještě se neřeší, není v pauze → nabídnout do fronty/dialogu
    // pausedList = po pokusu běží pauza T → do štítku (badge nabídne „Dotáhnout" hned)
    // (položky v aktivním řešení do štítku nedáváme — už se řeší)
    const fresh = tf.filter((it) => !beingHandled(sk, it) && !inRetryPause(it))
    const pausedList = tf.filter((it) => !beingHandled(sk, it) && inRetryPause(it))
    incomplete.set(sk, pausedList.map((it) => toEntry(it, rc)))
    if (fresh.length === 0) return

    const job = {
      toFill: fresh,
      retryCtx: rc,
      onSettled: (item, resp, filled) => {
        // Po KAŽDÉM pokusu (i neúspěšném):
        // 1) pauza T min proti opakování fill POSTu
        useFillAttemptStore.getState().record(attemptKey(item), rc)
        // 2) aplikace výsledku do zdroje — merge řady z odpovědi + úklid toFill
        settled?.(item, resp, filled)
        // 3) pryč ze štítku (kdyby ji tam mezitím dal jiný běh)
        useIncompleteDataStore.getState().remove(sk, attemptKey(item))
      },
    }

    if (md === 'auto') useFillQueueStore.getState().enqueue({ key: sk, jobs: [job] })
    else useFillPromptStore.getState().offer({ key: sk, job })
  })

  // ── Effect 2: odchod z obrazovky / přepnutí do 'off' ──
  useEffect(() => {
    if (mode === 'off') {
      useFillQueueStore.getState().cancelKey(screenKey)
      useFillPromptStore.getState().withdraw(screenKey)
      // registr NEČISTIT — v 'off' je badge jediný způsob, jak data dotáhnout
    }
    return () => {
      useFillQueueStore.getState().cancelKey(screenKey)
      useFillPromptStore.getState().withdraw(screenKey)
      useIncompleteDataStore.getState().remove(screenKey)
    }
  }, [screenKey, mode])
}

// ── Varianta 1: React Query zdroj (tabulky) ──
export function useToFill(queryKey, data, mergeFilled, retryCtx = {}) {
  const queryClient = useQueryClient()
  const key = JSON.stringify(queryKey)

  // Po KAŽDÉM fill POSTu — BEZ refetche celé query (ta by znovu stáhla všechny řady).
  // Fill odpověď nese `data` = aktuální stav té jedné řady z DB, a to i při
  // filled:false (up_to_date — data mezitím přibyla jinou cestou). Stačí ji mergnout.
  //   - resp.data je pole  → mergeFilled nahradí řadu v result
  //   - filled / up_to_date → položka pryč z toFill (řada je kompletní)
  //   - jinak (chyba 403/síť, source_unavailable, empty…) → položka v toFill zůstane;
  //     Effect 1 ji díky record() uvidí „v pauze" → štítek s „Dotáhnout".
  const apply = (item, resp, filled) => {
    const hasData = Array.isArray(resp?.data)
    const done = filled || resp?.skipped_reason === 'up_to_date'
    queryClient.setQueryData(queryKey, (prev) => {
      if (!prev) return prev
      return {
        ...prev,
        result: hasData ? mergeFilled(prev.result, resp, item) : prev.result,
        toFill: done ? (prev.toFill ?? []).filter((it) => sig(it) !== sig(item)) : prev.toFill,
      }
    })
  }

  useRegisterApplier(key, apply)
  useToFillCore(key, data?.toFill, retryCtx, apply)
}

// ── Varianta 2: meta.toFill z grafu ──
// enabled — graf se vůbec nevykresluje (např. neaktivní záložka Graf), takže
// meta je stale hodnota z dřívějška; nesmí se zpracovávat (viz doc/plans/
// fill-attempt-cache.md — badge na záložce Data nesmí vidět toFill z Grafu).
export function useChartToFill(screenKey, meta, retryCtx = {}, enabled = true) {
  // Graf nemá klientská data k merge ani lokální toFill k úklidu — po dotažení se
  // přenačte přes useChartFillBust (lastFilledAt → chartUrl &_fill= → ChartFrame
  // refetch → nová meta.toFill). onSettled proto jen no-op.
  useToFillCore(screenKey, enabled ? meta?.toFill : undefined, retryCtx, () => {})
}

// ── Varianta 3: víc grafů na jedné obrazovce (MeasGrafTable, PeriodGrafView) ──
// Každý graf má vlastní klíč (chartKey = URL grafu bez cache-bustu). Graf předá
// svou meta přes setMeta(chartKey, meta), toFill všech grafů se sloučí do jedné
// fronty pod screenKey. Graf se přenačte (bust(chartKey) → nový &_fill=) teprve
// když ve frontě nezbývá žádná JEHO řada a aspoň jedna se reálně dotáhla —
// ne po každé řadě a nezávisle na ostatních grafech (globální useChartFillBust
// by přenačetl všechny grafy až po doběhnutí celé fronty).
//
//   chartKeys — klíče aktuálně vykreslených grafů (meta jiných klíčů = stale, ignoruje se)
//   enabled   — grafy se nevykreslují (záložka Data / gate) → nic nezpracovávat
export function useChartsToFill(screenKey, chartKeys, retryCtx = {}, enabled = true) {
  const [metas, setMetas] = useState({})   // { [chartKey]: meta|null }
  const [busts, setBusts] = useState({})   // { [chartKey]: timestamp }
  const filledRef = useRef({})             // { [chartKey]: true } — od posledního přenačtení se něco dotáhlo

  const setMeta = useCallback((chartKey, meta) => {
    setMetas((p) => (p[chartKey] === meta ? p : { ...p, [chartKey]: meta }))
  }, [])

  // Nabízet až když mají meta VŠECHNY grafy (i neúspěšné → null). Grafy se načítají
  // postupně — průběžné nabízení by po přijetí prvních řad vyvolalo další dialog
  // pro řady grafů, které doběhly později.
  const allLoaded = chartKeys.every((k) => metas[k] !== undefined)

  // toFill per graf (jen aktuální grafy) + sloučený seznam bez duplicit
  const perChart = chartKeys.map((k) => [k, metas[k]?.toFill ?? []])
  const seen = new Set()
  const merged = []
  for (const [, items] of perChart) {
    for (const it of items) {
      const s = sig(it)
      if (!seen.has(s)) { seen.add(s); merged.push(it) }
    }
  }

  const perChartRef = useRef(perChart)
  perChartRef.current = perChart

  // Po každém fill POSTu (fronta i „Dotáhnout" ve štítku): u grafů, které řadu
  // obsahují, zkontroluj, jestli ještě nějaká jejich řada čeká ve frontě.
  // Právě zpracovaná položka je ve frontě ještě 'pending' → vyřadit podle sig.
  const onSettled = (item, _resp, filled) => {
    const s = sig(item)
    const queue = useFillQueueStore.getState()
    const done = []
    for (const [k, items] of perChartRef.current) {
      if (!items.some((it) => sig(it) === s)) continue
      if (filled) filledRef.current[k] = true
      const waiting = items.some((it) => sig(it) !== s && queue.has(screenKey, it))
      if (!waiting && filledRef.current[k]) {
        filledRef.current[k] = false
        done.push(k)
      }
    }
    if (done.length) {
      const now = Date.now()
      setBusts((b) => {
        const n = { ...b }
        done.forEach((k) => { n[k] = now })
        return n
      })
    }
  }

  useRegisterApplier(screenKey, onSettled)
  useToFillCore(screenKey, enabled && allLoaded ? merged : undefined, retryCtx, onSettled)

  const bust = (chartKey) => (busts[chartKey] ? `&_fill=${busts[chartKey]}` : '')
  return { setMeta, bust }
}
